import { useCallback, useEffect, useRef } from 'react';
import { personalHubSocket } from '../../../../../../utils/Socket';
import { resumeAll, registerSocket, unregisterSocket } from '../../../../../wsRecovery/wsResume';
import { startHeartbeat, stopHeartbeat } from '../../../../../wsRecovery/heartbeat';
import { getPersonalHubWsDiff, personalHubWsLastSeen } from '../api/personalHubWsApi';

/**
 * Roster-style WS hook for personal hub sections (factions reputations, hangar, blueprints).
 */
export default function usePersonalHubSectionSocket({
  section,
  joinEvent,
  leaveEvent,
  joinPayload = null,
  enabled = false,
  isLoggedIn = false,
  onRefetch,
  resumeRegistryKey,
}) {
  const lastResumeIdRef = useRef(0);
  const enabledRef = useRef(enabled);
  const joinPayloadRef = useRef(joinPayload);
  enabledRef.current = enabled;
  joinPayloadRef.current = joinPayload;

  const refetchAndSyncPointer = useCallback(async () => {
    if (!enabledRef.current || !isLoggedIn) return;
    if (typeof onRefetch !== 'function') return;

    const maxSeq = await onRefetch();
    const seq = Number(maxSeq || 0);
    if (seq > 0) {
      await personalHubWsLastSeen(section, seq).catch(() => {});
      lastResumeIdRef.current = Math.max(lastResumeIdRef.current, seq);
    }
  }, [isLoggedIn, onRefetch, section]);

  useEffect(() => {
    if (!resumeRegistryKey) return undefined;

    registerSocket(resumeRegistryKey, () => {
      if (!enabledRef.current || !isLoggedIn) return;
      if (!personalHubSocket.connected) {
        personalHubSocket.connect();
        startHeartbeat(personalHubSocket);
      }
      refetchAndSyncPointer();
    });

    return () => {
      enabledRef.current = false;
      unregisterSocket(resumeRegistryKey);
    };
  }, [isLoggedIn, refetchAndSyncPointer, resumeRegistryKey]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return undefined;

    if (!personalHubSocket.connected) {
      personalHubSocket.connect();
      startHeartbeat(personalHubSocket);
    }

    const onConnect = () => {
      personalHubSocket.emit(joinEvent, joinPayloadRef.current || undefined);
      resumeAll();
    };

    const onReconnect = async () => {
      personalHubSocket.emit(joinEvent, joinPayloadRef.current || undefined);
      try {
        const diff = await getPersonalHubWsDiff(section);
        if (diff?.needRefresh) {
          await refetchAndSyncPointer();
        } else if (diff?.maxWsLogSeq) {
          lastResumeIdRef.current = Math.max(
            lastResumeIdRef.current,
            Number(diff.maxWsLogSeq)
          );
        }
      } catch (err) {
        console.error(`[PersonalHub:${section}] reconnect diff failed:`, err);
      }
    };

    personalHubSocket.on('connect', onConnect);
    personalHubSocket.on('reconnect', onReconnect);

    return () => {
      stopHeartbeat(personalHubSocket);
      personalHubSocket.off('connect', onConnect);
      personalHubSocket.off('reconnect', onReconnect);
    };
  }, [enabled, isLoggedIn, joinEvent, refetchAndSyncPointer, section]);

  useEffect(() => {
    const handler = () => {
      if (!isLoggedIn || !enabled) return;
      if (!personalHubSocket.connected) {
        personalHubSocket.connect();
        startHeartbeat(personalHubSocket);
      }
    };
    window.addEventListener('auth-ws-refreshed', handler);
    return () => window.removeEventListener('auth-ws-refreshed', handler);
  }, [enabled, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return undefined;

    const resyncIfStale = async () => {
      try {
        const diff = await getPersonalHubWsDiff(section);
        if (diff?.needRefresh) {
          await refetchAndSyncPointer();
        }
      } catch (err) {
        console.error(`[PersonalHub:${section}] visibility/online diff failed:`, err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      resyncIfStale();
    };

    const onOnline = () => {
      resyncIfStale();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, isLoggedIn, refetchAndSyncPointer, section]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return undefined;

    if (!personalHubSocket.connected) {
      personalHubSocket.connect();
      startHeartbeat(personalHubSocket);
    }

    personalHubSocket.emit(joinEvent, joinPayloadRef.current || undefined);

    const onMissed = (payload) => {
      if (payload?.section && payload.section !== section) return;
      refetchAndSyncPointer();
    };

    const onUpdate = (payload) => {
      if (payload?.section && payload.section !== section) return;
      refetchAndSyncPointer();
    };

    personalHubSocket.on('personalHubMissed', onMissed);
    personalHubSocket.on('personalHubUpdate', onUpdate);

    return () => {
      personalHubSocket.off('personalHubMissed', onMissed);
      personalHubSocket.off('personalHubUpdate', onUpdate);
      if (leaveEvent) {
        personalHubSocket.emit(leaveEvent, joinPayloadRef.current || undefined);
      }
    };
  }, [enabled, isLoggedIn, joinEvent, leaveEvent, refetchAndSyncPointer, section]);

  return { lastResumeIdRef };
}
